<template>
  <div class="team-profile-page">
    <!-- Hero Section with Background Image -->
    <div class="hero-section">
      <div class="hero-background">
        <img
          src="../assets/dummy.jpg"
          alt="Professional Business Background"
          class="hero-bg-img"
        />
        <div class="hero-overlay"></div>
      </div>
    </div>

    <!-- Profile Card -->
    <div class="profile-section">
      <div class="container">
        <div class="profile-card">
          <div class="profile-avatar">
            <img
              :src="member?.image || '/placeholder.jpg'"
              :alt="member?.name"
              class="avatar-img"
              @error="onImgError"
            />
          </div>
          <div class="profile-info">
            <h1 class="profile-name">{{ member?.name || 'Loading...' }}</h1>
            <p class="profile-position">{{ member?.position || '' }}</p>
            <div class="profile-contact">
              <div v-if="member?.email" class="contact-item">
                <i class="fas fa-envelope"></i>
                <span>{{ member.email }}</span>
              </div>
              <div v-if="member?.phone" class="contact-item">
                <i class="fas fa-phone"></i>
                <span>{{ member.phone }}</span>
              </div>
              <div v-if="member?.linkedin" class="contact-item">
                <i class="fab fa-linkedin"></i>
                <a :href="member.linkedin" target="_blank" rel="noopener noreferrer" class="linkedin-link">LinkedIn Profile</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- About Section -->
    <div class="about-section">
      <div class="container">
        <div class="about-card">
          <h2 class="about-title">About {{ member?.name || 'Team Member' }}</h2>
          <div v-if="member?.bio" class="about-content">
            <p v-for="(para, idx) in splitBio(member.bio)" :key="idx" class="about-para">{{ para }}</p>
          </div>
          <div v-if="member?.experience" class="about-section">
            <h3 class="section-title">Experience</h3>
            <ul class="experience-list">
              <li v-for="(exp, i) in asArray(member.experience)" :key="i" class="experience-item">{{ exp }}</li>
            </ul>
          </div>
          <div v-if="member?.skills" class="about-section">
            <h3 class="section-title">Skills & Expertise</h3>
            <div class="skills-container">
              <span v-for="(skill, i) in asArray(member.skills)" :key="i" class="skill-tag">{{ skill }}</span>
            </div>
          </div>
          <div class="back-section">
            <router-link to="/team" class="back-button">
              <i class="fas fa-arrow-left"></i>
              Back to Team
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const member = ref(null)
const loading = ref(true)

const fetchTeamMember = async () => {
  try {
    const response = await fetch(`https://www.franchisebrigade.com/admin/api/team/${route.params.id}`)
    if (!response.ok) {
      throw new Error('Failed to fetch team member')
    }
    const data = await response.json()
    member.value = data
  } catch (error) {
    // Fallback data
    member.value = {
      id: route.params.id,
      name: 'Team Member',
      position: 'Professional',
      image: '/placeholder.jpg',
      bio: 'Experienced professional dedicated to helping entrepreneurs find the perfect franchise match.',
      joined: new Date().toISOString(),
      email: 'member@franchisebrigade.com',
      phone: '+1 (555) 123-4567',
      linkedin: 'https://linkedin.com/in/team-member',
      experience: ['5+ years in franchise industry', 'Expert in business development'],
      skills: ['Business Strategy', 'Franchise Consulting', 'Market Analysis']
    }
  } finally {
    loading.value = false
  }
}

const onImgError = (e) => {
  const target = e.target
  if (!target.src.endsWith('/placeholder.jpg')) {
    target.src = '/placeholder.jpg'
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const splitBio = (bio) => {
  return bio.split(/\r?\n/).filter(para => para.trim())
}

const asArray = (val) => Array.isArray(val) ? val : [val]

onMounted(() => {
  fetchTeamMember()
})
</script>

<style scoped>
.team-profile-page {
  min-height: 100vh;
  background: #f8f9fa;
}

/* Hero Section */
.hero-section {
  position: relative;
  height: 400px;
  overflow: hidden;
}

.hero-background {
  position: relative;
  width: 100%;
  height: 100%;
}

.hero-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%);
}

/* Profile Section */
.profile-section {
  margin-top: -80px;
  position: relative;
  z-index: 10;
  padding-bottom: 40px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.profile-card {
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  padding: 40px;
  display: flex;
  align-items: flex-start;
  gap: 30px;
  position: relative;
}

.profile-avatar {
  flex-shrink: 0;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  overflow: hidden;
  border: 5px solid white;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-info {
  flex: 1;
  min-width: 0;
}

.profile-name {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 10px 0;
  line-height: 1.2;
}

.profile-position {
  font-size: 1.3rem;
  color: #da0e0e;
  font-weight: 600;
  margin: 0 0 25px 0;
}

.profile-contact {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  color: #666;
}

.contact-item i {
  width: 20px;
  color: #da0e0e;
}

.linkedin-link {
  color: #0077b5;
  text-decoration: none;
  font-weight: 500;
}

.linkedin-link:hover {
  text-decoration: underline;
}

/* About Section */
.about-section {
  padding: 40px 0;
}

.about-card {
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.08);
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
}

.about-title {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 30px 0;
  text-align: center;
}

.about-content {
  margin-bottom: 40px;
}

.about-para {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #555;
  margin-bottom: 20px;
  text-align: justify;
}

.about-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
  border-bottom: 2px solid #da0e0e;
  padding-bottom: 10px;
}

.experience-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.experience-item {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 12px;
  padding-left: 20px;
  position: relative;
}

.experience-item::before {
  content: "•";
  color: #da0e0e;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.skill-tag {
  background: #da0e0e;
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-block;
}

.back-section {
  text-align: center;
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #eee;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #da0e0e;
  color: white;
  padding: 12px 25px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(218, 14, 14, 0.3);
}

.back-button:hover {
  background: #b30b0b;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(218, 14, 14, 0.4);
}

.back-button i {
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-section {
    height: 300px;
  }
  
  .profile-card {
    flex-direction: column;
    text-align: center;
    padding: 30px 20px;
  }
  
  .profile-avatar {
    width: 150px;
    height: 150px;
    margin: 0 auto;
  }
  
  .profile-name {
    font-size: 2rem;
  }
  
  .profile-position {
    font-size: 1.1rem;
  }
  
  .about-card {
    padding: 30px 20px;
  }
  
  .about-title {
    font-size: 1.5rem;
  }
  
  .about-para {
    font-size: 1rem;
  }
  
  .contact-item {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .profile-name {
    font-size: 1.8rem;
  }
  
  .profile-avatar {
    width: 120px;
    height: 120px;
  }
  
  .hero-section {
    height: 250px;
  }
}
</style> 